import { api, ensureTokenLife } from './client';
import { uploadProgressWorks } from '@/lib/nativeHttp';
import { UPLOAD_IDLE_MARK, createIdleWatch, uploadPatience } from '@/lib/uploadWatch';

/**
 * Wrappers for files-service.
 *
 * The frontend never talks directly to MinIO/S3. Instead it POSTs a multipart
 * form to /api/uploads (routed by the gateway to files-service) and gets back
 * a stable URL. The URL is then attached to the parent entity via that
 * entity's own endpoint — e.g. PUT /auth/me/avatar { avatar_url: <url> }.
 */

export type UploadCategory =
  | 'avatars'
  | 'homework'
  | 'homework_audio'
  | 'lesson_covers'
  | 'lesson_media'
  | 'stories'
  | 'vocab_audio';

export type UploadResult = {
  key: string;
  url: string;
  content_type: string;
  size: number;
  filename: string;
};

/**
 * Сколько отправлено — 0…100 или `null`, когда размер неизвестен.
 *
 * ⚠️ `null` — НЕ НОЛЬ. Без `total` (сжатая отправка, старый браузер) честного
 * числа нет, и показывать надо «Отправляю…» без процента.
 */
export type UploadProgress = (percent: number | null) => void;

export const uploadsApi = {
  /**
   * Отправить файл.
   *
   * ⚠️ ПРОЦЕНТЫ БЕРУТСЯ У САМОГО AXIOS, и это не случайность: в браузере он
   * ходит через XHR, у которого есть событие прогресса отправки. `fetch` его не
   * даёт вовсе — перевод загрузки на `fetch` тихо лишил бы ученика полосы.
   *
   * ⚠️ В ПРИЛОЖЕНИИ С PINNING ПРОЦЕНТА НЕТ (02.10.2026). Там файл уходит одним вызовом CapacitorHttp, и события
   * прогресса отправки не бывает (`lib/nativeHttp.ts`). Полоса стояла бы на «0 %» до самого конца — поэтому
   * сразу сообщаем `null`: экран показывает «Отправляем…» без числа, как при неизвестном размере.
   */
  async upload(
    file: File | Blob,
    category: UploadCategory,
    filename?: string,
    onProgress?: UploadProgress,
    /**
     * `clientId` — имя записи голоса (договор `audio-upload.md` §2): повтор с тем же именем и тем же размером
     * сервер узнаёт и возвращает тот же адрес, второй файл не пишет. Полем формы, не заголовком:
     * незаявленный заголовок браузер отменил бы сам.
     */
    opts: { clientId?: string } = {},
  ): Promise<UploadResult> {
    const form = new FormData();
    form.append('category', category);
    form.append(
      'file',
      file,
      filename ?? (file instanceof File ? file.name : `${category}-${Date.now()}.bin`),
    );
    if (opts.clientId) form.append('client_id', opts.clientId);

    const percentKnown = uploadProgressWorks();
    if (!percentKnown) onProgress?.(null);

    // ⚠️ СКОЛЬКО ЖДАТЬ (02.10.2026, `lib/uploadWatch.ts`). Процент есть — общего предела нет вовсе, обрываем
    // только по простою: пока байты идут, медленная связь не причина начинать заново (раньше стояло 120 с на
    // всё, и запись на слабой связи не уходила никогда). Процента нет — простой мерить нечем, остаётся
    // общий предел.
    const patience = uploadPatience(percentKnown);

    // ⚠️ КЛЮЧ — НА ВСЮ ЗАГРУЗКУ (30.10.2026, T5). Ключ проверяется после приёма всего тела (`ensureTokenLife`),
    // поэтому он должен пережить загрузку целиком: предел плюс минута, а без предела (сайт, мерим простой) — пять
    // минут. Ключ берётся в момент отправки, а не записи. Продление — до сторожа простоя: его время не в счёт.
    await ensureTokenLife(patience.totalMs > 0 ? patience.totalMs + 60_000 : 5 * 60_000);

    const controller = patience.mode === 'idle' ? new AbortController() : undefined;
    let idled = false;
    const watch = createIdleWatch(patience.idleMs, () => {
      idled = true;
      controller?.abort();
    });

    try {
      const { data } = await api.post('/uploads', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: patience.totalMs,
        signal: controller?.signal,
        // Обработчик стоит и без колбэка: по его событиям сторож простоя видит, что байты идут.
        onUploadProgress: percentKnown
          ? (e: { loaded: number; total?: number }) => {
              watch.touch(e.loaded);
              if (!onProgress) return;
              const total = typeof e.total === 'number' && e.total > 0 ? e.total : null;
              onProgress(total === null ? null : Math.min(100, Math.floor((e.loaded / total) * 100)));
            }
          : undefined,
      });
      return data as UploadResult;
    } catch (e) {
      // Оборвали сами, по простою: наружу — как обрыв связи (без кода ответа), очередь повторит.
      if (idled) throw Object.assign(new Error('upload stalled'), { status: 0, code: UPLOAD_IDLE_MARK });
      throw e;
    } finally {
      watch.stop();
    }
  },

  async presign(key: string): Promise<{ url: string; expires_in_seconds: number }> {
    const { data } = await api.get(`/uploads/${encodeURIComponent(key)}/presign`);
    return data;
  },

  async remove(key: string): Promise<void> {
    await api.delete(`/uploads/${encodeURIComponent(key)}`);
  },

  // ----- Video / streaming (P4.1-4.2) -----

  /**
   * Resolves an HLS-bundle storage key to a fully-signed manifest URL.
   * The lesson player feeds this URL into hls.js / native HLS.
   *
   * The endpoint rewrites each segment line in the m3u8 to a fresh presigned
   * URL — sharing the manifest out-of-band still doesn't grant long-term
   * access because every segment URL is dead within 5 minutes.
   */
  videoManifestURL(key: string): string {
    // We return the URL synchronously so it can be passed straight to a
    // <video src> binding. The browser will hit our endpoint with the
    // JWT cookie/Authorization header that the rest of /api uses.
    //
    // Must use the SAME base path as the axios client (default /api/v1) so the
    // gateway strips /api/v1 and routes to files-service's /videos/:key/manifest
    // route. The previous /api/videos path was missing the /v1 segment and would
    // not be routed by the gateway.
    const basePath = import.meta.env.VITE_API_BASE_PATH || '/api/v1';
    return `${basePath}/videos/${encodeURIComponent(key)}/manifest`;
  },

  /**
   * Returns a fresh presigned URL for a one-off asset (audio attachment,
   * lesson cover image). 10-minute TTL.
   */
  async signedAssetURL(key: string): Promise<{ url: string; expires_in_seconds: number }> {
    const { data } = await api.get(`/videos/${encodeURIComponent(key)}/signed`);
    return data;
  },
};
